import type { SeedSizeFloorKind } from '@parcel/filters';
import { Field, FieldGroup, FieldLabel, Input, MultiSelect } from '@parcel/ui';
import * as React from 'react';
import { MenuSelect } from '../where-bar/menu-select.js';
import {
  SECTOR_OPTIONS,
  SIZE_FLOOR_KINDS,
  STAGE_OPTIONS,
  STAKEHOLDER_OPTIONS,
  type ScoringAnswers,
} from './scoring-answers.js';

const SECTORS = SECTOR_OPTIONS.map((d) => ({ value: d.value, label: d.label }));
const STAGES = STAGE_OPTIONS.map((d) => ({ value: d.value, label: d.label }));
const STAKEHOLDERS = STAKEHOLDER_OPTIONS.map((d) => ({ value: d.value, label: d.label }));
const FLOOR_PLACEHOLDER: Record<SeedSizeFloorKind, string> = {
  units: 'e.g. 50',
  sqft: 'e.g. 20,000',
  cost: 'e.g. 5,000,000',
};

/** The five onboarding answers the three scores are seeded from. */
export function ScoringAnswersForm({
  answers,
  cities,
  disabled,
  inDialog,
  onChange,
}: {
  answers: ScoringAnswers;
  cities: readonly string[];
  disabled?: boolean;
  /** Inside a modal dialog the pickers' lists must be modal too, or they cannot scroll. */
  inDialog?: boolean;
  onChange: (next: ScoringAnswers) => void;
}) {
  const set = <K extends keyof ScoringAnswers>(key: K, value: ScoringAnswers[K]) =>
    onChange({ ...answers, [key]: value });
  // A seeded market outside the city list still shows, so it can be seen and removed.
  const marketOptions = React.useMemo(() => {
    const known = new Set(cities);
    return [...cities, ...answers.markets.filter((m) => !known.has(m))].map((city) => ({
      value: city,
      label: city,
    }));
  }, [cities, answers.markets]);
  return (
    <FieldGroup className="gap-5 text-left">
      <Field>
        <FieldLabel htmlFor="scoring-markets">Focus markets</FieldLabel>
        <MultiSelect
          id="scoring-markets"
          options={marketOptions}
          value={answers.markets}
          onValueChange={(value) => set('markets', value)}
          placeholder="Select markets"
          searchPlaceholder="Search cities…"
          disabled={disabled}
          modal={inDialog}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="scoring-sectors">Sectors</FieldLabel>
        <MultiSelect
          id="scoring-sectors"
          options={SECTORS}
          value={answers.sectors}
          onValueChange={(value) => set('sectors', value)}
          placeholder="Select sectors"
          disabled={disabled}
          modal={inDialog}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="scoring-stages">Target stages</FieldLabel>
        <MultiSelect
          id="scoring-stages"
          options={STAGES}
          value={answers.stages}
          onValueChange={(value) => set('stages', value)}
          placeholder="Select stages"
          disabled={disabled}
          modal={inDialog}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="scoring-stakeholders">Who you sell to</FieldLabel>
        <MultiSelect
          id="scoring-stakeholders"
          options={STAKEHOLDERS}
          value={answers.stakeholders}
          onValueChange={(value) => set('stakeholders', value)}
          placeholder="Select firm types"
          disabled={disabled}
          modal={inDialog}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="scoring-size-floor">Size floor (optional)</FieldLabel>
        <div className="flex gap-2">
          <MenuSelect
            label="Size floor measure"
            value={answers.sizeFloor.kind}
            options={SIZE_FLOOR_KINDS}
            disabled={disabled}
            onChange={(kind) =>
              set('sizeFloor', { ...answers.sizeFloor, kind: kind as SeedSizeFloorKind })
            }
            className="h-10 w-48 px-3 text-sm"
          />
          <Input
            id="scoring-size-floor"
            inputMode="numeric"
            placeholder={FLOOR_PLACEHOLDER[answers.sizeFloor.kind]}
            value={answers.sizeFloor.min}
            disabled={disabled}
            onChange={(event) =>
              set('sizeFloor', { ...answers.sizeFloor, min: event.target.value })
            }
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Projects above it score higher. Leave it empty to skip.
        </p>
      </Field>
    </FieldGroup>
  );
}
